import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { classService } from '../../../services';

// URL state parsers
const searchParser = parseAsString.withDefault('');
const gradeParser = parseAsString.withDefault('');
const statusParser = parseAsString.withDefault('');
const teacherParser = parseAsString.withDefault('');
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

const useClass = ({schoolId}: {schoolId: string}) => {
  const queryClient = useQueryClient();

  // URL state management với nuqs
  const [search, setSearch] = useQueryState('search', searchParser);
  const [grade, setGrade] = useQueryState('grade', gradeParser);
  const [status, setStatus] = useQueryState('status', statusParser);
  const [teacher, setTeacher] = useQueryState('teacher', teacherParser);
  const [page, setPage] = useQueryState('page', pageParser);
  const [per_page, setPerPage] = useQueryState('per_page', perPageParser);

  // Tạo filters object từ URL state với type mặc định là "class"
  const filters = useMemo(() => ({
    type: 'class', // Luôn filter theo type class
    search: search || undefined,
    grade: grade || undefined,
    status: (status as 'active' | 'inactive' | 'archived' | '') || undefined,
    teacher: teacher || undefined,
    schoolId,
  }), [search, grade, status, teacher, schoolId]);

  // Query key cho caching
  const queryKey = ['classroom', filters];

  // Fetch classes với react-query
  const {
    data: classesData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    // Không gửi page/per_page: ClassList không có phân trang, BE thiếu pagination thì trả đủ lớp.
    queryFn: () => classService.getClasses(filters),
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút (cacheTime cũ)
  });

  // Create class mutation
  const createClassMutation = useMutation({
    mutationFn: (data: any) => classService.createClass({ ...data, type: 'class' }),
    onSuccess: (newClass) => {
      // Invalidate và refetch classes list
      queryClient.invalidateQueries({ queryKey: ['classroom'] });
    },
  });

  // Filter và pagination handlers
  const updateFilters = useCallback((newFilters: Partial<typeof filters>) => {
    if (newFilters.search !== undefined) setSearch(newFilters.search || null);
    if (newFilters.grade !== undefined) setGrade(newFilters.grade || null);
    if (newFilters.status !== undefined) setStatus(newFilters.status || null);
    if (newFilters.teacher !== undefined) setTeacher(newFilters.teacher || null);
    // Reset về trang 1 khi filter thay đổi
    setPage(1);
  }, [setSearch, setGrade, setStatus, setTeacher, setPage]);

  const updatePagination = useCallback((newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  }, [setPage, setPerPage]);

  const resetFilters = useCallback(() => {
    setSearch(null);
    setGrade(null);
    setStatus(null);
    setTeacher(null);
    setPage(1);
  }, [setSearch, setGrade, setStatus, setTeacher, setPage]);

  // Clear specific filter
  const clearFilter = useCallback((filterKey: keyof typeof filters) => {
    switch (filterKey) {
      case 'search':
        setSearch(null);
        break;
      case 'grade':
        setGrade(null);
        break;
      case 'status':
        setStatus(null);
        break;
      case 'teacher':
        setTeacher(null);
        break;
    }
    setPage(1);
  }, [setSearch, setGrade, setStatus, setTeacher, setPage]);

  return {
    // Data
    classes: classesData?.data || [],
    pagination: classesData?.pagination,
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,

    // Mutations
    createClass: createClassMutation.mutate,

    // Mutation states
    isCreating: createClassMutation.isPending,

    // Mutation objects for advanced handling
    createClassMutation,
  };
};

export default useClass;
